import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import { entitiesAPI, breachReportsAPI, exportAPI, tasksAPI } from '../../../services/api';
import { CreateTempGuestDialog } from '../../../components/guest/CreateTempGuestDialog';
import { TasksCountBadge } from '../../../components/shared/TasksCountBadge';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Plus, Search, Trash2, MoreHorizontal, Eye, AlertTriangle,
  BarChart3, ShieldOff, Calendar, Clock, Edit, Building2,
  FileText, AlertCircle, Shield, X, FileDown, Loader2, Copy
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format, differenceInHours, parseISO, subHours, isSameMonth, differenceInDays, startOfMonth, endOfMonth } from 'date-fns';
import { cn } from '../../../lib/utils';

// Status configuration with colors
const STATUS_CONFIG = {
  reported: { label: 'Reported', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  investigating: { label: 'Investigating', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  not_a_breach: { label: 'Not a Breach', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  assessing: { label: 'Assessing', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  closed: { label: 'Closed', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  submitted: { label: 'Submitted', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
};

// Risk level configuration
const RISK_CONFIG = {
  low: { label: 'Low', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-500' },
  medium: { label: 'Medium', color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-500' },
  high: { label: 'High', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-500' },
  critical: { label: 'Critical', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-500' },
};

// Calculate hours remaining/overdue from deadline
const getDeadlineStatus = (deadline) => {
  if (!deadline) return null;
  try {
    const deadlineDate = parseISO(deadline);
    const now = new Date();
    const hoursLeft = differenceInHours(deadlineDate, now);
    return hoursLeft;
  } catch {
    return null;
  }
};

export default function DataBreachPage() {
  const navigate = useNavigate();
  const { user, isDPO, isComplianceReadOnly: isReadOnly, isClientAdmin, isEntityAdmin } = useAuth();
  const [breachReports, setBreachReports] = useState([]);
  const [taskCounts, setTaskCounts] = useState({});
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedRisk, setSelectedRisk] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [pendingDpoOnly, setPendingDpoOnly] = useState(false);
  
  // Summary dialog
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [selectedBreach, setSelectedBreach] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const [intakeLinkDialogOpen, setIntakeLinkDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [reportsRes, entitiesRes, countsRes] = await Promise.all([
        breachReportsAPI.getAll(),
        (isDPO || isClientAdmin) ? entitiesAPI.getAll(isClientAdmin ? user?.client_id : undefined) : Promise.resolve({ data: [] }),
        tasksAPI.getCountsBatch('breach').catch(() => ({ data: {} })),
      ]);
      setBreachReports(reportsRes.data);
      if (isDPO || isClientAdmin) setEntities(entitiesRes.data);
      if (isEntityAdmin && user?.entity_id) setEntities([{ id: user.entity_id, entity_name: user.entity_name || 'Your Entity' }]);
      setTaskCounts(countsRes.data || {});
    } catch (error) {
      toast.error('Failed to fetch breach data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this breach report?')) return;
    try {
      await breachReportsAPI.delete(id);
      toast.success('Breach report deleted');
      setSummaryDialogOpen(false);
      setSelectedBreach(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete breach report');
    }
  };

  const handleExportDocx = async (reportId, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await exportAPI.breachReportDocx(reportId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || `Breach_Report_${reportId}.docx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Breach report exported');
    } catch { toast.error('Failed to export breach report'); }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedBreach) return;
    try {
      setUpdatingStatus(true);
      await breachReportsAPI.update(selectedBreach.id, {
        ...selectedBreach,
        status: newStatus
      });
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      // Update local state
      setSelectedBreach(prev => ({ ...prev, status: newStatus }));
      setBreachReports(prev => prev.map(b => 
        b.id === selectedBreach.id ? { ...b, status: newStatus } : b
      ));
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleRowClick = (report) => {
    setSelectedBreach(report);
    setSummaryDialogOpen(true);
  };

  const getEntityName = (entityId) => {
    const entity = entities.find(e => e.id === entityId || e.entity_uuid === entityId);
    return entity?.entity_name || entity?.name || 'Unknown';
  };

  // Calculate stats with secondary metrics for S-01
  const stats = useMemo(() => {
    const openStatuses = ['reported', 'investigating', 'assessing', 'draft', 'in_progress'];
    const openBreaches = breachReports.filter(b => openStatuses.includes(b.status));
    const openCount = openBreaches.length;

    let overdueCount = 0;
    let notifyingCount = 0;
    let overdueRef = null;
    let maxOverdueHours = -Infinity;
    let totalNotifyingHours = 0;
    let notifyingBreaches = 0;

    breachReports.forEach(b => {
      if (b.regulator_deadline || b.awareness_date) {
        const deadline = b.regulator_deadline || (b.awareness_date ? (() => {
          const d = new Date(b.awareness_date);
          d.setHours(d.getHours() + 72);
          return d.toISOString();
        })() : null);

        if (deadline) {
          const hoursLeft = getDeadlineStatus(deadline);
          if (hoursLeft !== null && hoursLeft < 0 && b.status !== 'closed' && b.status !== 'not_a_breach') {
            overdueCount++;
            if (-hoursLeft > maxOverdueHours) {
              maxOverdueHours = -hoursLeft;
              overdueRef = b.breach_reference || b.id;
            }
          }
          if (hoursLeft !== null && hoursLeft >= 0 && hoursLeft <= 24 && b.status !== 'closed' && b.status !== 'not_a_breach') {
            notifyingCount++;
            totalNotifyingHours += hoursLeft;
            notifyingBreaches++;
          }
        }
      }
    });

    // S-01: Count open breaches past 48h threshold
    const now = new Date();
    const past48h = subHours(now, 48);
    const past48hCount = openBreaches.filter(b => {
      const createdDate = b.created_at ? parseISO(b.created_at) : null;
      return createdDate && createdDate < past48h;
    }).length;

    // S-01: Closed this month vs last month
    const currentMonth = new Date();
    const lastMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const closedThisMonth = breachReports.filter(b => {
      const closedDate = b.updated_at ? parseISO(b.updated_at) : null;
      return (b.status === 'closed' || b.status === 'not_a_breach') && closedDate && isSameMonth(closedDate, currentMonth);
    }).length;
    const closedLastMonth = breachReports.filter(b => {
      const closedDate = b.updated_at ? parseISO(b.updated_at) : null;
      return (b.status === 'closed' || b.status === 'not_a_breach') && closedDate && isSameMonth(closedDate, lastMonthStart);
    }).length;
    const closedDelta = closedThisMonth - closedLastMonth;

    const closedCount = breachReports.filter(b => b.status === 'closed' || b.status === 'not_a_breach').length;
    const avgNotifyingHours = notifyingBreaches > 0 ? Math.round(totalNotifyingHours / notifyingBreaches) : 0;

    // S-03: Count pending DPO decision (unclassified or awaiting Step 7 decision)
    const pendingDpoCount = breachReports.filter(b =>
      b.is_personal_data_breach === null || // Unclassified
      b.dpo_reporting_decision === 'pending' ||
      b.dpo_reporting_decision === null ||
      !b.dpo_reporting_decision
    ).length;

    return {
      open: openCount,
      past48h: past48hCount,
      overdue: overdueCount,
      overdueRef,
      maxOverdueHours: maxOverdueHours === -Infinity ? 0 : maxOverdueHours,
      notifying: notifyingCount,
      avgNotifyingHours,
      closed: closedCount,
      closedThisMonth,
      closedDelta,
      pendingDpo: pendingDpoCount,
    };
  }, [breachReports]);

  // S-06: Analytics data
  const analytics = useMemo(() => {
    // 1. Breaches by month (last 6 months)
    const now = new Date();
    const months = [];
    const monthCounts = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = format(d, 'MMM');
      months.push(monthKey);
      monthCounts[monthKey] = 0;
    }

    breachReports.forEach(b => {
      if (b.created_at) {
        const createdDate = parseISO(b.created_at);
        const monthKey = format(createdDate, 'MMM');
        if (months.includes(monthKey)) {
          monthCounts[monthKey]++;
        }
      }
    });

    const monthData = months.map(m => ({ name: m, count: monthCounts[m] }));
    const maxMonthCount = Math.max(...monthData.map(m => m.count), 1);

    // 2. Top root causes
    const rootCauseCounts = {};
    breachReports.forEach(b => {
      if (b.root_causes && Array.isArray(b.root_causes)) {
        b.root_causes.forEach(cause => {
          rootCauseCounts[cause] = (rootCauseCounts[cause] || 0) + 1;
        });
      }
    });

    const topCauses = Object.entries(rootCauseCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([cause, count]) => ({
        name: cause.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        count,
        percent: breachReports.length > 0 ? Math.round((count / breachReports.length) * 100) : 0,
      }));

    const maxCauseCount = Math.max(...topCauses.map(c => c.count), 1);

    // 3. Notification compliance rate
    const reportableBreaches = breachReports.filter(b => b.dpo_reporting_decision === 'report');
    const notifiedOnTime = reportableBreaches.filter(b => {
      if (!b.date_notified || !b.confirmed_deadline) return false;
      const notifiedDate = parseISO(b.date_notified);
      const deadlineDate = parseISO(b.confirmed_deadline);
      return notifiedDate <= deadlineDate;
    }).length;

    const complianceRate = reportableBreaches.length > 0
      ? Math.round((notifiedOnTime / reportableBreaches.length) * 100)
      : 0;

    return {
      monthData,
      maxMonthCount,
      topCauses,
      maxCauseCount,
      complianceRate,
      notifiedOnTime,
      reportableCount: reportableBreaches.length,
    };
  }, [breachReports]);

  // Filter reports
  const filteredReports = useMemo(() => {
    return breachReports.filter(report => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch = 
          (report.title || '').toLowerCase().includes(search) ||
          (report.breach_reference || '').toLowerCase().includes(search) ||
          (report.what_happened || '').toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      
      // Entity filter
      if (selectedOrg !== 'all' && report.entity_id !== selectedOrg) return false;
      
      // Status filter
      if (selectedStatus !== 'all' && report.status !== selectedStatus) return false;
      
      // Risk filter
      if (selectedRisk !== 'all' && report.final_risk_level?.toLowerCase() !== selectedRisk) return false;
      
      // Overdue filter
      if (overdueOnly) {
        const deadline = report.regulator_deadline || (report.awareness_date ? (() => {
          const d = new Date(report.awareness_date);
          d.setHours(d.getHours() + 72);
          return d.toISOString();
        })() : null);

        if (!deadline) return false;
        const hoursLeft = getDeadlineStatus(deadline);
        if (hoursLeft === null || hoursLeft >= 0) return false;
      }

      // S-05: Pending DPO filter
      if (pendingDpoOnly) {
        const isPendingDpo = report.is_personal_data_breach === null ||
          report.dpo_reporting_decision === 'pending' ||
          report.dpo_reporting_decision === null ||
          !report.dpo_reporting_decision;
        if (!isPendingDpo) return false;
      }

      return true;
    });
  }, [breachReports, searchTerm, selectedOrg, selectedStatus, selectedRisk, overdueOnly, pendingDpoOnly]);

  // Get risk score for display
  const getRiskDisplay = (report) => {
    if (!report) return null;
    const level = report.final_risk_level || report.risk_level;
    if (!level) return null;
    
    const score = report.override_severity && report.override_likelihood 
      ? report.override_severity * report.override_likelihood 
      : report.auto_matrix_score;
    
    return { level: level.toLowerCase(), score };
  };

  // Get deadline display
  const getDeadlineDisplay = (report) => {
    const deadline = report.regulator_deadline || (report.awareness_date ? (() => {
      const d = new Date(report.awareness_date);
      d.setHours(d.getHours() + 72);
      return d.toISOString();
    })() : null);
    
    if (!deadline) return null;
    
    const hoursLeft = getDeadlineStatus(deadline);
    if (hoursLeft === null) return null;
    
    const isOverdue = hoursLeft < 0;
    const absHours = Math.abs(hoursLeft);
    
    return {
      isOverdue,
      text: isOverdue ? `${absHours} hours overdue` : `${absHours} hours left`,
      hours: absHours,
      deadline,
    };
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="breach-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" data-testid="page-title">Data Breaches</h1>
            <p className="text-muted-foreground text-sm">Caribbean Multi-Jurisdiction Compliance</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="analytics-btn">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </Button>
            {!isReadOnly && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setIntakeLinkDialogOpen(true)}
                  data-testid="send-intake-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Send Intake Link
                </Button>
                <Button onClick={() => navigate('/compliance/breaches/workflow/new')} data-testid="report-breach-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Report Breach
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards with Secondary Metrics (S-01) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Open Breaches */}
          <Card className={cn("border-l-4", stats.open > 0 ? "border-l-blue-500" : "border-l-gray-300")}>
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400" data-testid="stat-open">
                {stats.open}
              </div>
              <div className="text-sm text-muted-foreground">Open Breaches</div>
              <div className="text-xs text-muted-foreground mt-2">
                {stats.past48h > 0 ? `${stats.past48h} past 48h threshold` : 'None past 48h'}
              </div>
            </CardContent>
          </Card>

          {/* Overdue Notifications */}
          <Card className={cn("border-l-4", stats.overdue > 0 ? "border-l-red-500" : "border-l-gray-300")}>
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-red-600 dark:text-red-400" data-testid="stat-overdue">
                {stats.overdue}
              </div>
              <div className="text-sm text-muted-foreground">Overdue Notifications</div>
              <div className="text-xs text-muted-foreground mt-2">
                {stats.overdueRef ? `${stats.overdueRef} overdue by ${stats.maxOverdueHours}h` : 'None overdue'}
              </div>
            </CardContent>
          </Card>

          {/* Currently Notifying */}
          <Card className={cn("border-l-4", stats.notifying > 0 ? "border-l-amber-500" : "border-l-gray-300")}>
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-amber-600 dark:text-amber-400" data-testid="stat-notifying">
                {stats.notifying}
              </div>
              <div className="text-sm text-muted-foreground">Currently Notifying</div>
              <div className="text-xs text-muted-foreground mt-2">
                {stats.notifying > 0 ? `Avg. ${stats.avgNotifyingHours}h remaining` : 'None notifying'}
              </div>
            </CardContent>
          </Card>

          {/* Closed This Month */}
          <Card className={cn("border-l-4", stats.closed > 0 ? "border-l-green-500" : "border-l-gray-300")}>
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-green-600 dark:text-green-400" data-testid="stat-closed">
                {stats.closedThisMonth}
              </div>
              <div className="text-sm text-muted-foreground">Closed This Month</div>
              <div className="text-xs text-muted-foreground mt-2">
                {stats.closedDelta > 0 ? `+${stats.closedDelta} vs last month` : stats.closedDelta < 0 ? `${stats.closedDelta} vs last month` : 'Same as last month'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* S-03: Pending DPO Decision Banner (conditional) */}
        {stats.pendingDpo > 0 && (
          <div className="mt-2 px-5 py-3 rounded-lg border-l-4 border-l-amber-500 border border-amber-200/50 dark:border-amber-800/30 bg-amber-50 dark:bg-amber-950/10 flex items-center justify-between gap-4">
            <span className="text-sm text-gray-900 dark:text-gray-100">
              <span className="font-semibold">{stats.pendingDpo} breach{stats.pendingDpo !== 1 ? 'es' : ''} pending DPO decision</span> — classification or notification status unconfirmed
            </span>
            <Button
              variant="link"
              size="sm"
              onClick={() => setSelectedStatus('assessing')}
              className="shrink-0 text-gray-900 dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300 font-medium p-0 h-auto"
            >
              Review queue →
            </Button>
          </div>
        )}

        {/* S-05: Filter Bar with Promoted Pills */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Input */}
          <div className="relative flex-1 min-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search breaches..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>

          {/* Filter Pills Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Pending DPO Pill */}
            <button
              onClick={() => setPendingDpoOnly(!pendingDpoOnly)}
              className={cn(
                "px-4 py-1.5 rounded-full text-sm font-medium transition-colors",
                pendingDpoOnly
                  ? "bg-primary text-primary-foreground"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"
              )}
              data-testid="pending-dpo-pill"
            >
              Pending DPO {pendingDpoOnly && <X className="inline h-3 w-3 ml-1" />}
            </button>

            {/* All Entities Dropdown */}
            {isDPO && (
              <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                <SelectTrigger className="w-auto border-0 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-4 py-1.5 h-auto font-medium text-sm hover:bg-gray-300 dark:hover:bg-gray-600" data-testid="client-filter">
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {entities.map(entity => (
                    <SelectItem key={entity.id} value={entity.id}>{entity.entity_name || entity.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* All Status Dropdown */}
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-auto border-0 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-4 py-1.5 h-auto font-medium text-sm hover:bg-gray-300 dark:hover:bg-gray-600" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="reported">Reported</SelectItem>
                <SelectItem value="investigating">Investigating</SelectItem>
                <SelectItem value="assessing">Assessing</SelectItem>
                <SelectItem value="not_a_breach">Not a Breach</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>

            {/* All Risk Dropdown */}
            <Select value={selectedRisk} onValueChange={setSelectedRisk}>
              <SelectTrigger className="w-auto border-0 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-4 py-1.5 h-auto font-medium text-sm hover:bg-gray-300 dark:hover:bg-gray-600" data-testid="risk-filter">
                <SelectValue placeholder="All Risk" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>

            {/* Overdue Only Pill */}
            <button
              onClick={() => setOverdueOnly(!overdueOnly)}
              className={cn(
                "px-4 py-1.5 rounded-full text-sm font-medium transition-colors",
                overdueOnly
                  ? "bg-primary text-primary-foreground"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"
              )}
              data-testid="overdue-pill"
            >
              Overdue only {overdueOnly && <X className="inline h-3 w-3 ml-1" />}
            </button>
          </div>
        </div>

        {/* S-06: Inline Analytics Strip (3-card grid) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Card 1: Breaches by Month */}
          <Card>
            <CardContent className="pt-6">
              <h4 className="text-sm font-semibold mb-4">Breaches by month (2026)</h4>
              <div className="flex items-end gap-1 h-12">
                {analytics.monthData.map((month) => {
                  const height = analytics.maxMonthCount > 0
                    ? (month.count / analytics.maxMonthCount) * 100
                    : 0;
                  const isLatest = month === analytics.monthData[analytics.monthData.length - 1].name;
                  return (
                    <div
                      key={month.name}
                      className={cn("flex-1 rounded-t transition-colors", {
                        'bg-teal-600 dark:bg-teal-500': isLatest,
                        'bg-teal-300 dark:bg-teal-600': !isLatest,
                      })}
                      style={{ height: `${height || 10}%` }}
                      title={`${month.name}: ${month.count}`}
                    />
                  );
                })}
              </div>
              <div className="text-xs text-muted-foreground mt-2 text-center">
                {analytics.monthData.map(m => m.name).join(' - ')}
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Top Root Causes */}
          <Card>
            <CardContent className="pt-6">
              <h4 className="text-sm font-semibold mb-4">Top root causes</h4>
              <div className="space-y-2">
                {analytics.topCauses.length > 0 ? (
                  analytics.topCauses.map((cause) => {
                    const barWidth = analytics.maxCauseCount > 0
                      ? (cause.count / analytics.maxCauseCount) * 100
                      : 0;
                    return (
                      <div key={cause.name} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium">{cause.name}</span>
                          <span className="text-muted-foreground">{cause.percent}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div className="h-full bg-teal-500 transition-all" style={{ width: `${barWidth}%` }} />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-xs text-muted-foreground">No root causes recorded</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Card 3: Notification Compliance Rate */}
          <Card>
            <CardContent className="pt-6">
              <h4 className="text-sm font-semibold mb-4">Notification compliance rate</h4>
              <div className="text-center">
                <div className="text-4xl font-bold text-teal-600 dark:text-teal-400 mb-2">
                  {analytics.complianceRate}%
                </div>
                <p className="text-xs text-muted-foreground">
                  {analytics.notifiedOnTime} of {analytics.reportableCount} reportable breaches notified on time
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* S-02: 72-Hour Deadline Tracker (conditional - list rows) */}
        {breachReports.some(b => {
          if (b.regulator_deadline || b.awareness_date) {
            const deadline = b.regulator_deadline || (b.awareness_date ? (() => {
              const d = new Date(b.awareness_date);
              d.setHours(d.getHours() + 72);
              return d.toISOString();
            })() : null);
            if (deadline) {
              const hoursLeft = getDeadlineStatus(deadline);
              return hoursLeft !== null && hoursLeft <= 72 && b.status !== 'closed' && b.status !== 'not_a_breach';
            }
          }
          return false;
        }) && (
          <Card>
            <CardContent className="p-0">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  72-hour deadline tracker
                </h3>
                <p className="text-xs text-muted-foreground">Active notification deadlines</p>
              </div>
              <div className="space-y-2 p-6">
                {breachReports.map(b => {
                  if (b.regulator_deadline || b.awareness_date) {
                    const deadline = b.regulator_deadline || (b.awareness_date ? (() => {
                      const d = new Date(b.awareness_date);
                      d.setHours(d.getHours() + 72);
                      return d.toISOString();
                    })() : null);

                    if (deadline && (b.status !== 'closed' && b.status !== 'not_a_breach')) {
                      const hoursLeft = getDeadlineStatus(deadline);
                      if (hoursLeft !== null && hoursLeft <= 72) {
                        const isOverdue = hoursLeft < 0;
                        const absHours = Math.abs(hoursLeft);
                        const riskConfig = RISK_CONFIG[b.final_risk_level?.toLowerCase() || 'low'] || RISK_CONFIG.low;

                        return (
                          <div key={b.id} className="px-4 py-3 bg-gray-50 dark:bg-gray-900/30 rounded-lg flex items-center justify-between gap-4 hover:bg-gray-100 dark:hover:bg-gray-900/50">
                            <div className="flex-1 min-w-0">
                              <p className="font-mono text-sm font-semibold">{b.breach_reference || b.id}</p>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{b.title || 'Untitled'}</p>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-muted-foreground truncate">{getEntityName(b.entity_id)}</p>
                            </div>
                            <div className="shrink-0">
                              <Badge className={riskConfig.bgColor}>{riskConfig.label} risk</Badge>
                            </div>
                            <div className="shrink-0">
                              <span className={cn("text-sm font-semibold whitespace-nowrap", {
                                'text-red-600 dark:text-red-400': isOverdue || hoursLeft < 12,
                                'text-amber-600 dark:text-amber-400': hoursLeft >= 12 && hoursLeft < 24,
                                'text-green-600 dark:text-green-400': hoursLeft >= 24,
                              })}>
                                {isOverdue ? `Overdue ${absHours}h` : `Due in ${absHours}h`}
                              </span>
                            </div>
                          </div>
                        );
                      }
                    }
                  }
                  return null;
                }).filter(Boolean)}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Breach Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Title</TableHead>
                  {isDPO && <TableHead>Entity</TableHead>}
                  <TableHead>Risk</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Days Open</TableHead>
                  <TableHead>Regulator Deadline</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isDPO ? 8 : 7} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                        Loading...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isDPO ? 8 : 7} className="p-0 border-0">
                      <div className="py-12 px-6">
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="relative mb-6">
                            <div className="absolute inset-0 bg-emerald-500/20 rounded-full blur-xl" />
                            <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border border-emerald-500/20">
                              <ShieldOff className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
                            </div>
                          </div>
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                            No breach incidents
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mb-6">
                            That's good news! When incidents occur, log them here to track investigation, notification, and remediation.
                          </p>
                          {!isReadOnly && (
                            <Button
                              onClick={() => navigate('/compliance/breaches/workflow/new')}
                              className="bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                            >
                              Report Breach
                            </Button>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReports.map((report) => {
                    const riskDisplay = getRiskDisplay(report);
                    const deadlineDisplay = getDeadlineDisplay(report);
                    const statusConfig = STATUS_CONFIG[report.status] || STATUS_CONFIG.draft;
                    
                    return (
                      <TableRow 
                        key={report.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(report)}
                        data-testid={`breach-row-${report.id}`}
                      >
                        <TableCell className="font-mono text-sm">
                          {report.breach_reference || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{report.title || 'Untitled'}</span>
                            <TasksCountBadge counts={taskCounts[report.id]} />
                          </div>
                        </TableCell>
                        {isDPO && (
                          <TableCell>{getEntityName(report.entity_id)}</TableCell>
                        )}
                        <TableCell>
                          {riskDisplay ? (
                            <div className="flex items-center gap-1.5">
                              <span className={cn(
                                "inline-flex items-center justify-center w-2 h-2 rounded-full",
                                RISK_CONFIG[riskDisplay.level]?.bgColor || "bg-gray-500"
                              )} />
                              <span className={cn("capitalize font-medium", RISK_CONFIG[riskDisplay.level]?.color)}>
                                {riskDisplay.level}
                                {riskDisplay.score && (
                                  <span className="text-muted-foreground ml-1">({riskDisplay.score})</span>
                                )}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("capitalize", statusConfig.color)} variant="secondary">
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        {/* S-04: Days Open with progress bar */}
                        <TableCell>
                          {report.created_at ? (() => {
                            const daysOpen = differenceInDays(new Date(), parseISO(report.created_at));
                            const progressPercent = Math.min((daysOpen / 30) * 100, 100);
                            const barColor = daysOpen <= 10 ? 'bg-green-500' : daysOpen <= 25 ? 'bg-amber-500' : 'bg-red-500';
                            return (
                              <div className="space-y-1">
                                <div className="text-sm font-medium">{daysOpen}</div>
                                <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div className={cn("h-full transition-all", barColor)} style={{ width: `${progressPercent}%` }} />
                                </div>
                              </div>
                            );
                          })() : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        {/* S-04: Regulator Deadline with relative time and hover tooltip */}
                        <TableCell>
                          {deadlineDisplay ? (
                            <div className="group relative">
                              <span className={cn(
                                "text-sm font-medium cursor-help",
                                deadlineDisplay.isOverdue ? "text-red-600 dark:text-red-400" : "text-muted-foreground"
                              )}>
                                {deadlineDisplay.isOverdue ? `Overdue ${Math.abs(deadlineDisplay.hours)}h` : `Due in ${deadlineDisplay.hours}h`}
                              </span>
                              <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                {format(parseISO(deadlineDisplay.deadline), 'MMM d, yyyy HH:mm')}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleRowClick(report); }}>
                                <Eye className="h-4 w-4 mr-2" />View Summary
                              </DropdownMenuItem>
                              {!isReadOnly && (
                                <>
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/compliance/breaches/workflow/${report.id}`); }}>
                                    <Edit className="h-4 w-4 mr-2" />Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); handleDelete(report.id); }}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuItem onClick={(e) => handleExportDocx(report.id, e)} data-testid={`export-breach-${report.id}`}>
                                <FileDown className="h-4 w-4 mr-2" />Export DOCX
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        
        {/* Breach Summary Dialog */}
        <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className={cn(
                  "h-5 w-5",
                  selectedBreach?.status === 'not_a_breach' ? "text-gray-500" :
                  getRiskDisplay(selectedBreach)?.level === 'critical' || getRiskDisplay(selectedBreach)?.level === 'high' 
                    ? "text-red-500" : "text-amber-500"
                )} />
                {selectedBreach?.title || 'Breach Summary'}
              </DialogTitle>
              <DialogDescription>
                {selectedBreach?.breach_reference ? (
                  <span className="font-mono">{selectedBreach.breach_reference}</span>
                ) : (
                  'No reference assigned'
                )}
              </DialogDescription>
            </DialogHeader>
            
            {selectedBreach && (
              <div className="space-y-6">
                {/* Key Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Building2 className="h-4 w-4" />
                      Entity
                    </div>
                    <p className="font-medium">{getEntityName(selectedBreach.entity_id)}</p>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      Awareness Date
                    </div>
                    <p className="font-medium">
                      {selectedBreach.awareness_date 
                        ? format(parseISO(selectedBreach.awareness_date), 'PPP')
                        : '-'}
                    </p>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <AlertCircle className="h-4 w-4" />
                      Risk Level
                    </div>
                    {getRiskDisplay(selectedBreach) ? (
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "inline-flex items-center justify-center w-3 h-3 rounded-full",
                          RISK_CONFIG[getRiskDisplay(selectedBreach).level]?.bgColor || "bg-gray-500"
                        )} />
                        <span className={cn("capitalize font-medium", RISK_CONFIG[getRiskDisplay(selectedBreach).level]?.color)}>
                          {getRiskDisplay(selectedBreach).level}
                          {getRiskDisplay(selectedBreach).score && (
                            <span className="text-muted-foreground ml-1">({getRiskDisplay(selectedBreach).score})</span>
                          )}
                        </span>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">Not assessed</p>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      Regulator Deadline
                    </div>
                    {(() => {
                      const deadline = getDeadlineDisplay(selectedBreach);
                      return deadline ? (
                        <p className={cn(
                          "font-medium",
                          deadline.isOverdue ? "text-red-600 dark:text-red-400" : ""
                        )}>
                          {deadline.text}
                        </p>
                      ) : (
                        <p className="text-muted-foreground">-</p>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Description */}
                {selectedBreach.what_happened && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FileText className="h-4 w-4" />
                      Description
                    </div>
                    <p className="text-sm bg-muted/50 p-3 rounded-lg">
                      {selectedBreach.what_happened}
                    </p>
                  </div>
                )}
                
                {/* Status Update */}
                <div className="space-y-2 pt-2 border-t">
                  <Label>Update Status</Label>
                  <Select 
                    value={selectedBreach.status} 
                    onValueChange={handleStatusChange}
                    disabled={updatingStatus}
                  >
                    <SelectTrigger data-testid="status-update-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="reported">Reported</SelectItem>
                      <SelectItem value="investigating">Investigating</SelectItem>
                      <SelectItem value="assessing">Assessing</SelectItem>
                      <SelectItem value="not_a_breach">Not a Breach</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Additional Info */}
                <div className="grid grid-cols-3 gap-4 text-sm pt-2 border-t">
                  <div>
                    <p className="text-muted-foreground">Breach Types</p>
                    <p className="font-medium">{selectedBreach.breach_types?.length || 0}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Data Categories</p>
                    <p className="font-medium">{selectedBreach.data_categories?.length || 0}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">People Affected</p>
                    <p className="font-medium">{selectedBreach.number_affected || 0}</p>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setSummaryDialogOpen(false)}>
                Close
              </Button>
              {!isReadOnly && (
                <Button
                  onClick={() => {
                    setSummaryDialogOpen(false);
                    navigate(`/compliance/breaches/workflow/${selectedBreach?.id}`);
                  }}
                  data-testid="edit-breach-btn"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Full Report
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Breach Intake Invite Dialog */}
        <CreateTempGuestDialog
          open={intakeLinkDialogOpen}
          onOpenChange={setIntakeLinkDialogOpen}
          defaultModule="breach_intake"
          entities={entities}
          lockedEntityId={isEntityAdmin ? user?.entity_id : null}
        />
      </div>
    </DashboardLayout>
  );
}
